/**
 * @file speakerIdentity.ts
 * @brief Name a transcript turn from its speaker letter.
 *
 * @details
 * Pure so it can be tested — this decision had a real bug in it. The session
 * row carries only `person_a_name` / `person_b_name`, which encodes an
 * assumption of exactly two voices. Medical diarization (15-09-2026) can now
 * produce a third: an interpreter, or a relative in the room. The previous
 * inline version was an `isA ? personA : personB` ternary, so every extra
 * voice was named with the CLAIMANT's name — one person's words attributed to
 * another on a document a court reads.
 *
 * @references
 * - docs/product/medical-assessment.md §3.2
 * - BACKEND/src/features/medical-assessment/capture/diarizedTranscript.mjs
 *
 * @author DES
 * @date 15-09-2026
 */

export type SpeakerIdentity = {
  /** Display name for the turn. */
  name: string;
  /** True only for the session's Person A — gates the avatar image. */
  isPersonA: boolean;
  /** True for A or B; a third voice has no profile to draw gender from. */
  isKnownPair: boolean;
};

/**
 * @param speaker Raw `utterances.speaker` value (any case, may be absent).
 * @param session The two names the session row knows about.
 */
export function resolveSpeakerIdentity(
  speaker: string | null | undefined,
  session: { person_a_name?: string | null; person_b_name?: string | null },
): SpeakerIdentity {
  const letter = (speaker ?? "").toUpperCase();

  const isPersonA =
    letter === "A" || letter === "PERSON_A" || letter.startsWith("A");
  const isPersonB =
    letter === "B" || letter === "PERSON_B" || letter.startsWith("B");

  if (isPersonA) {
    return {
      name: session.person_a_name ?? "Person A",
      isPersonA: true,
      isKnownPair: true,
    };
  }
  if (isPersonB) {
    return {
      name: session.person_b_name ?? "Person B",
      isPersonA: false,
      isKnownPair: true,
    };
  }
  // Never borrow the claimant's name for a voice we cannot identify.
  return {
    name: `Speaker ${letter || "?"}`,
    isPersonA: false,
    isKnownPair: false,
  };
}
